import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { CallWidgetView } from '../../types'
import { MaximizedAICallWidget, MinimizedAICallWidget } from './components'
import { removeCall, upsertActiveAvailableCall } from 'store/callCenterSlice'
import { getInfoRequestVoiceAgentLoad } from 'utils/get-load-detail'
import axios from 'axios'
import { useAuth } from 'hooks/auth'
import { addNotification } from 'store/notificationSlice'
import { useDispatch } from 'react-redux'
import { Category } from 'hooks/useNotification'
import { ConnectionState, RemoteAudioTrack, Room, RoomEvent } from 'livekit-client'
import { ClientCall, CallStatus, VoiceAgentTool, VoiceToolCollectLoadDetailsParams, VoiceToolName, VoiceTranscript, VoiceTranscriptRole } from '@numeo/types'
import { showError } from 'utils/snackbar'
import { useAppSelector } from 'hooks'
import { waitFor } from 'utils/wait-for'

interface AICallWidgetProps {
    callId: string
    index: number
    listeningCallId: string | null
    setListeningCallId: Dispatch<SetStateAction<string | null>>
}
export const AICallWidget = ({ callId, index, listeningCallId, setListeningCallId }: AICallWidgetProps) => {
    const [view, setView] = useState<CallWidgetView>(CallWidgetView.MINIMIZED)
    const { application } = useAuth()
    const [dispatcherRequestAlert, setDispatcherRequestAlert] = useState(false)
    const [rateHighlight, setRateHighlight] = useState(false)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [takenOver, setTakenOver] = useState(false)
    const takenOverRef = useRef(false)
    const [loadingTakeOver, setLoadingTakeOver] = useState(false)
    const [isMinimizing, setIsMinimizing] = useState(false)
    const [transcriptions, setTranscriptions] = useState<VoiceTranscript[]>([])
    const { activeAvailableCalls } = useAppSelector((state) => state.callCenter)
    const activeAvailableCall = activeAvailableCalls[callId]
    const activeAvailableCallRef = useRef(activeAvailableCall)
    const dispatch = useDispatch()
    const callInitialized = useRef(false)
    const livekitRoom = useRef<Room | null>(null)
    const livekitRoomAttachedTracks = useRef<HTMLMediaElement[]>([])
    const [endingCall, setEndingCall] = useState(false)
    const roomName = useRef('')
    const isListening = listeningCallId === callId
    const isListeningRef = useRef(isListening)
    const initializationFailed = useRef(false)

    const onTranscriptionReceived = (transcription: VoiceTranscript) => {
        setTranscriptions((prev) => {
            const updatedTranscriptions = [...prev]
            const latest = updatedTranscriptions.at(-1)
            if (latest?.role === transcription.role) {
                latest.content += ` ${transcription.content}`
            } else {
                updatedTranscriptions.push(transcription)
            }
            return updatedTranscriptions
        })
    }

    const updateCallStatus = (callStatus: CallStatus) => {
        if (callStatus === activeAvailableCallRef.current.call?.status) {
            return
        }

        dispatch(
            upsertActiveAvailableCall({
                callId,
                call: {
                    ...activeAvailableCallRef.current.call,
                    status: callStatus,
                } as ClientCall,
            })
        )
    }

    const joinLivekitRoom = async (roomName: string) => {
        try {
            const response = await axios.post(`/voip/sipclient/livekit-room/${roomName}`)
            const { token, serverURL } = response.data.data
            const room = new Room()
            await room.connect(serverURL, token)
            livekitRoom.current = room

            // Subscribe to remote participant audio tracks
            room.remoteParticipants.forEach((participant) => {
                participant.audioTrackPublications.forEach((publication) => {
                    const attachedTrack = publication.track?.attach()
                    updateCallStatus(CallStatus.CONNECTED)

                    if (attachedTrack) {
                        attachedTrack.muted = !isListeningRef.current
                        livekitRoomAttachedTracks.current = [...(livekitRoomAttachedTracks.current || []), attachedTrack]
                    }
                })
            })
            room.on(RoomEvent.TrackSubscribed, (track) => {
                if (track instanceof RemoteAudioTrack) {
                    const attachedTrack = track.attach()
                    updateCallStatus(CallStatus.CONNECTED)

                    attachedTrack.muted = !isListeningRef.current
                    livekitRoomAttachedTracks.current = [...(livekitRoomAttachedTracks.current || []), attachedTrack]
                }
            })

            // Detect when broker (SIP participant) hangs up after Take Over
            room.on(RoomEvent.ParticipantDisconnected, (participant) => {
                if (participant.identity.startsWith('sip-') && takenOverRef.current) {
                    // Broker hung up after Take Over - clean up
                    console.log('CallWidget:: Broker hung up after Take Over, cleaning up')
                    cleanUpOnCallEnded()
                }
            })

            // Subscribe to call status changes
            room.registerTextStreamHandler('CALL_STATUS_CHANGE', async (reader) => {
                const text = await reader.readAll()
                const receivedStatus = CallStatus[text.toUpperCase() as keyof typeof CallStatus]
                if (receivedStatus) {
                    updateCallStatus(receivedStatus)
                }

                if ([CallStatus.ENDED, CallStatus.BUSY].includes(text as CallStatus)) {
                    cleanUpOnCallEnded()
                }
            })

            // Subscribe to transcriptions
            room.registerTextStreamHandler('user_transcriptions', async (reader) => {
                const text = await reader.readAll()

                onTranscriptionReceived({
                    content: text,
                    role: VoiceTranscriptRole.USER,
                })
            })
            room.registerTextStreamHandler('tool', async (reader) => {
                const text = await reader.readAll()
                const toolData = JSON.parse(text) as VoiceAgentTool
                if (toolData.toolName === VoiceToolName.COLLECT_LOAD_DETAILS && (toolData.parameters as VoiceToolCollectLoadDetailsParams).rate) {
                    setRateHighlight(true)
                }

                if (toolData.toolName === VoiceToolName.REQUEST_HUMAN_DISPATCHER) {
                    setDispatcherRequestAlert(true)
                }

                onTranscriptionReceived({
                    content: '',
                    tool: toolData,
                    role: VoiceTranscriptRole.TOOL,
                })
            })
            let lastTranscriptionMsg = ''
            room.registerTextStreamHandler('lk.transcription', async (reader, participantInfo) => {
                const message = await reader.readAll()
                if (reader.info.attributes?.['lk.transcribed_track_id'] && reader.info.attributes?.['lk.transcription_final'] === 'true') {
                    const receivedMessage = `${participantInfo.identity}: ${message}`
                    if (receivedMessage !== lastTranscriptionMsg && participantInfo.identity.startsWith('agent')) {
                        onTranscriptionReceived({
                            content: message,
                            role: VoiceTranscriptRole.ASSISTANT,
                        })
                        lastTranscriptionMsg = receivedMessage
                    }
                }
            })
        } catch (ex) {
            console.error('AICallWidget::joinLivekitRoom:: Failed to join livekit room', ex)
        }
    }

    const initializeAICall = async () => {
        if (callInitialized.current || !activeAvailableCall.load?._id) return
        callInitialized.current = true

        roomName.current = `ai|${application?.projectName}|${callId}`

        try {
            joinLivekitRoom(roomName.current)
            const response = await axios.post(
                `/voip/sipclient/make-plivo-call`,
                {
                    callId: callId,
                    to: activeAvailableCall.phoneNumber,
                    metadata: {
                        load: getInfoRequestVoiceAgentLoad(activeAvailableCall.load),
                    },
                    roomName: roomName.current,
                },
                { timeout: 30000 }
            )
            const { call } = response.data.data
            dispatch(upsertActiveAvailableCall({ callId, call: { ...call, status: activeAvailableCallRef.current.call?.status || CallStatus.INITIATING } }))
        } catch (error) {
            console.error('CallWidget::initializeAICall::', error)
            initializationFailed.current = true
            dispatch(removeCall(callId))
            switch ((error as { status: number }).status) {
                case 403:
                    dispatch(
                        addNotification({
                            id: '',
                            message: 'Connection expired, please reconnect your account',
                            type: 'error',
                            category: Category.Error,
                            timestamp: Date.now(),
                            topic: 'temp',
                        })
                    )
                    break
                case 429:
                    dispatch(
                        addNotification({
                            id: '',
                            message: 'All available phone numbers are in use, please wait for a moment or connect your own phone number',
                            type: 'error',
                            category: Category.Error,
                            timestamp: Date.now(),
                            topic: 'temp',
                        })
                    )
                    break
                default:
                    dispatch(
                        addNotification({
                            id: '',
                            message: 'Failed to initiate AI call. Please try again',
                            type: 'error',
                            category: Category.Error,
                            timestamp: Date.now(),
                            topic: 'temp',
                        })
                    )
            }
        }
    }

    const onMaximize = () => {
        setIsTransitioning(true)
        setTimeout(() => {
            setView(CallWidgetView.MAXIMIZED)
            setTimeout(() => setIsTransitioning(false), 300)
        }, 50)
    }

    const onMinimize = () => {
        setIsMinimizing(true)
        setTimeout(() => {
            setView(CallWidgetView.MINIMIZED)
            setIsMinimizing(false)
        }, 250)
    }

    const cleanUpOnCallEnded = async () => {
        try {
            await livekitRoom.current?.disconnect()
            if (!initializationFailed.current) {
                showError('ClientCall ended')
            }
        } catch (error) {
            console.error('CallWidget::onEndCall:: Failed to disconnect livekit room', error)
        }

        dispatch(removeCall(callId))
    }

    const onEndCall = async () => {
        setEndingCall(true)
        try {
            if (takenOver) {
                // After Take Over, agent is gone - call API directly to remove SIP participant
                await axios.post('/voip/sipclient/end-takeover-call', {
                    roomName: roomName.current,
                    callId,
                    projectName: application?.projectName,
                })
            } else {
                // Normal flow - send to agent
                await waitFor({ condition: () => livekitRoom.current?.state === ConnectionState.Connected, interval: 1000, maxRetries: 5 })
                await livekitRoom.current?.localParticipant.sendText('hangup_call', { topic: 'hangup_call' })
            }
        } catch (error) {
            console.error('CallWidget::onEndCall:: Failed to end call', error)
        }
        await cleanUpOnCallEnded()
        setEndingCall(false)
    }

    const toggleListening = () => {
        setListeningCallId(listeningCallId === callId ? null : callId)
    }

    const onTakeOver = async () => {
        setLoadingTakeOver(true)
        try {
            livekitRoom.current?.localParticipant.sendText('AGENT_DISCONNECT_REQUEST', { topic: 'AGENT_DISCONNECT_REQUEST' })
            if (livekitRoom.current && livekitRoom.current.localParticipant) {
                await livekitRoom.current.localParticipant.setMicrophoneEnabled(true)
            }
        } catch (error) {
            console.error('CallWidget::onTakeOver::', error)
            showError('Failed to take over call, please try again')
        }
        setLoadingTakeOver(false)
        setTakenOver(true)
        takenOverRef.current = true // Sync ref for event listeners
        setDispatcherRequestAlert(false)
    }

    useEffect(() => {
        activeAvailableCallRef.current = activeAvailableCall
    }, [activeAvailableCall])

    useEffect(() => {
        isListeningRef.current = isListening
        livekitRoomAttachedTracks.current?.forEach((track) => {
            track.muted = listeningCallId !== callId
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listeningCallId])

    useEffect(() => {
        initializeAICall()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAvailableCall?.load?._id])

    if (!activeAvailableCall) {
        return null
    }

    switch (view) {
        case CallWidgetView.MAXIMIZED:
            return (
                <MaximizedAICallWidget
                    onMinimize={onMinimize}
                    takenOver={takenOver}
                    loadingTakeOver={loadingTakeOver}
                    isListening={isListening}
                    isMinimizing={isMinimizing}
                    onTakeOver={onTakeOver}
                    onEndCall={onEndCall}
                    toggleListening={toggleListening}
                    transcriptions={transcriptions}
                    callId={callId}
                    endingCall={endingCall}
                />
            )
        case CallWidgetView.MINIMIZED:
            return (
                <MinimizedAICallWidget
                    onMaximize={onMaximize}
                    index={index}
                    dispatcherRequestAlert={dispatcherRequestAlert}
                    rateHighlight={rateHighlight}
                    isTransitioning={isTransitioning}
                    takenOver={takenOver}
                    loadingTakeOver={loadingTakeOver}
                    isListening={isListening}
                    onTakeOver={onTakeOver}
                    onEndCall={onEndCall}
                    toggleListening={toggleListening}
                    transcriptions={transcriptions}
                    callId={callId}
                    endingCall={endingCall}
                />
            )
        default:
            return null
    }
}
